import clsx from 'clsx';
import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Paper, Grid, Divider, Typography, Tooltip } from '@mui/material';
import FeedbackService from 'services/FeedbackService';
import RatingStars from '../RatingStars';
import { useStyles } from './styles';
import { byFips } from 'country-code-lookup';

const Props = {
  feedback: PropTypes.shape({
    id: PropTypes.number.isRequired,
    score: PropTypes.string.isRequired,
    reason: PropTypes.string,
    date: PropTypes.string.isRequired,
    approved: PropTypes.bool,
    user: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string.isRequired,
    }),
  }),
  onChange: PropTypes.func.isRequired,
};

export default function AdminCard({ feedback, onChange, onEditClick }) {
  const classes = useStyles();
  const feedbackService = new FeedbackService();
  const country = feedback.user?.countryCode ? byFips(feedback.user.countryCode) : null;

  function getActionButtons() {
    if (feedback.approved === null) {
      return [
        { label: 'Approve', value: true },
        { label: 'Disapprove', value: false },
      ];
    }

    if (feedback.approved) {
      return [
        { label: 'Approved', value: true },
        { label: 'Disapprove', value: false },
      ];
    }

    return [
      { label: 'Approve', value: true },
      { label: 'Disapproved', value: false },
    ];
  }

  async function handleUpdate(data) {
    try {
      await feedbackService.update(feedback.id, {
        approved: data.approved === feedback.approved ? null : data.approved,
      });

      onChange();
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Paper className={classes.root} elevation={0} variant="outlined">
      <Grid container={true} alignItems="center">
        <Grid item={true} xs={6} md={4}>
          <Box pr={1}>
            {feedback.user.name && (
              <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={feedback.user.name}
                placement="bottom-start"
              >
                <Typography className={classes.overflowText}>
                  <b>{feedback.user.name}</b>
                </Typography>
              </Tooltip>
            )}

            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              title={feedback.user.institution || ''}
              placement="bottom-start"
            >
              <Typography className={classes.overflowText}>{feedback.user.institution}</Typography>
            </Tooltip>

            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              title={country?.country || ''}
              placement="bottom-start"
            >
              <Typography className={classes.overflowText}>{country?.country}</Typography>
            </Tooltip>
          </Box>
        </Grid>
        <Grid item={true} xs={6} md={2}>
          <Tooltip
            classes={{ tooltip: classes.tooltip }}
            title={feedback.user.email}
            placement="bottom-start"
          >
            <Typography>
              <b>{feedback.user.email}</b>
            </Typography>
          </Tooltip>

          <Typography variant="body2">{moment(feedback.date).format('DD/MM/YYYY')}</Typography>
        </Grid>

        <Grid className={classes.buttonsColumn} item={true} xs={6} md={6}>
          {getActionButtons().map((button, index) => (
            <Button
              key={index}
              className={clsx(classes.button, {
                [classes.primaryColor]: feedback.approved === button.value,
              })}
              onClick={() => handleUpdate({ approved: button.value })}
            >
              {button.label}
            </Button>
          ))}
        </Grid>
      </Grid>

      {!!feedback.reason && (
        <>
          <Divider className={classes.divider} />

          <Typography>"{feedback.reason}"</Typography>
        </>
      )}

      <Divider className={classes.divider} />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <RatingStars size="medium" value={Math.ceil(Number(feedback.score) / 2)} />
        </Box>

        <Button className={classes.button} onClick={onEditClick}>
          Edit comment
        </Button>
      </Box>
    </Paper>
  );
}

AdminCard.propTypes = Props;
